import React, { FC, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';

import type { LiveStreamEntries } from '../../types';
import type { ReduxState, ThunkDispatchType } from 'app/redux/types';

import { IconSize } from 'app/design-system/components/Icon';
import { useTranslation } from 'app/lang';
import usePrevious from 'app/lib/hooks/usePrevious';
import { replacingHyphenOnDash } from 'app/lib/replaceDashSymbol';
import { type OpenLoginDialog, openLoginDialog } from 'app/modules/Signing/actions';
import { selectIsLoggedIn } from 'app/redux/selectors/auth';
import Dropdown, { type ChangeHandler } from 'app/widgets/Dropdown/Single';

import type { SelectStreamWithAutoplay } from '../../actions';
import { selectStreamWithAutoplay } from '../../actions';
import { selectStreamId, streamsByLiveMatchesSelector } from '../../selectors';
import type { DropdownOption } from './DropdownComponents';
import { DropdownComponents } from './DropdownComponents';

import dictionary from './MatchesDropdown.dictionary.json';

import styles from './MatchesDropdown.module.scss';

// ---

type StoreProps = {
  entries: LiveStreamEntries;
  streamId: number | null;
  isSignedIn: boolean;
};

type DispatchProp = {
  dispatch: ThunkDispatchType<SelectStreamWithAutoplay | OpenLoginDialog>;
};

const MatchesDropdown: FC<DispatchProp & StoreProps> = ({
  entries,
  streamId,
  dispatch,
  isSignedIn,
}) => {
  const { t } = useTranslation();
  const previousEntries = usePrevious(entries);
  const isStreamEntitiesChanged = JSON.stringify(previousEntries) !== JSON.stringify(entries);

  const options = useMemo(() => {
    const ids = new Set();

    return entries.reduce((acc: DropdownOption[], entry) => {
      if (!entry.matchId || !entry.sportId) {
        console.error(`MatchesDropdown -> matchId = ${entry.matchId} or sportId = ${entry.sportId} - not found`);
        return acc;
      }

      const hasSportId = ids.has(entry.sportId);

      if (!hasSportId) {
        ids.add(entry.sportId);
      }

      acc.push({
        label: replacingHyphenOnDash(entry.matchName),
        value: entry.matchId,
        sportId: entry.sportId,
        matchId: entry.matchId,
        showSportTile: !hasSportId,
        topMatch: !!(entry.isLeading),
      });

      return acc;
    }, [])
      .sort((a, b) => a.sportId - b.sportId);
  }, [isStreamEntitiesChanged]);

  const selectOptionHandler: ChangeHandler<DropdownOption> = useCallback((option) => {
    if (!isSignedIn) {
      dispatch(openLoginDialog());
      return;
    }

    if (!option || !('value' in option)) {
      console.error('MatchesDropdown -> selectOptionHandler -> `option` not found', option);
      return;
    }

    dispatch(selectStreamWithAutoplay(option.value));
  }, [isSignedIn]);

  const value = options.find((option) => option.value === streamId) || null;

  return (
    <Dropdown<DropdownOption>
      dynamicOptions
      menuPortalTarget={process.env.SERVER ? null : document.body}
      placeholder={t(dictionary.placeholder)}
      isSearchable={false}
      options={options}
      value={value}
      className={styles.dropdown}
      indicatorSize={IconSize.MEDIUM}
      onChange={selectOptionHandler}
      components={DropdownComponents(entries, isStreamEntitiesChanged)} />
  );
};

const mapStateToProps = (state: ReduxState): StoreProps => ({
  entries: streamsByLiveMatchesSelector(state),
  streamId: selectStreamId(state),
  isSignedIn: selectIsLoggedIn(state),
});

export default connect(mapStateToProps)(MatchesDropdown);
